import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Star } from "@phosphor-icons/react";

export function SurveyModal({ milestone, open, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return toast.error("Please select a rating");
    try {
      setSubmitting(true);
      await api.post(`/milestones/${milestone.id}/survey`, { rating, comment });
      toast.success("Thank you for your feedback!");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="survey-modal">
        <DialogHeader>
          <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Milestone Feedback
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="border border-[#E5E5E5] p-4">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-1">Milestone</p>
            <p className="text-sm font-semibold text-[#0A0A0A]">{milestone?.title}</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">
              How satisfied are you with this milestone delivery?
            </Label>
            <div className="flex items-center gap-2 py-2" data-testid="survey-rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110 active:scale-95"
                  data-testid={`survey-star-${n}`}
                >
                  <Star
                    size={32}
                    weight={(hoverRating || rating) >= n ? "fill" : "regular"}
                    className={(hoverRating || rating) >= n ? "text-[#D97706]" : "text-[#E5E5E5]"}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="text-sm font-bold text-[#0A0A0A] ml-2">
                  {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">
              Additional comments (optional)
            </Label>
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share your thoughts on this delivery..."
              className="rounded-none border-[#E5E5E5] min-h-[80px]"
              data-testid="survey-comment"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10"
            data-testid="survey-submit-button"
          >
            {submitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SurveyResults({ surveys }) {
  if (!surveys || surveys.length === 0) return null;

  const avgRating = surveys.reduce((sum, s) => sum + s.rating, 0) / surveys.length;

  return (
    <div className="space-y-3" data-testid="survey-results">
      <div className="flex items-center gap-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
          Client Satisfaction
        </h3>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <Star key={n} size={14} weight={avgRating >= n ? "fill" : "regular"} className={avgRating >= n ? "text-[#D97706]" : "text-[#E5E5E5]"} />
          ))}
          <span className="text-sm font-bold text-[#0A0A0A] ml-1">{avgRating.toFixed(1)}</span>
          <span className="text-xs text-[#A3A3A3]">({surveys.length})</span>
        </div>
      </div>
      <div className="space-y-2">
        {surveys.map(s => (
          <div key={s.id} className="border border-[#E5E5E5] p-3" data-testid={`survey-result-${s.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} size={12} weight={s.rating >= n ? "fill" : "regular"} className={s.rating >= n ? "text-[#D97706]" : "text-[#E5E5E5]"} />
                ))}
              </div>
              <span className="text-xs font-semibold text-[#0A0A0A]">{s.submitted_by_name}</span>
              <span className="text-[10px] text-[#A3A3A3]">{s.milestone_title}</span>
            </div>
            {s.comment && <p className="text-xs text-[#525252]">{s.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
